"use client";

import React, { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Eye,
  Edit,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  CheckSquare,
  Square,
  Crown,
} from "lucide-react";
import { formatCurrency, formatDate } from "@/lib/utils";
import {
  Appointment,
  Customer,
  Vehicle,
  Staff,
  AppointmentStatus,
} from "@/types";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { AppointmentEditDialog } from "./AppointmentEditDialog";

interface AppointmentsTableProps {
  appointments: Appointment[];
  customers: Customer[];
  vehicles: Vehicle[];
  staff: Staff[];
  getStatusBadge: (status: string) => React.ReactElement;
  onStatusUpdate: (
    appointmentId: string,
    newStatus: AppointmentStatus
  ) => Promise<void>;
  onView: (appointment: Appointment) => void;
  isLoading?: boolean;
}

const ITEMS_PER_PAGE = 10;

type SortField = "date" | "amount" | "customer" | null;
type SortDirection = "asc" | "desc";

export function AppointmentsTable({
  appointments,
  customers,
  vehicles,
  staff,
  getStatusBadge,
  onStatusUpdate,
  onView,
  isLoading = false,
}: AppointmentsTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [statusUpdateDialog, setStatusUpdateDialog] = useState<{
    open: boolean;
    appointmentId: string;
    newStatus: AppointmentStatus | null;
  }>({ open: false, appointmentId: "", newStatus: null });
  const [editingAppointment, setEditingAppointment] =
    useState<Appointment | null>(null);

  // Sorting logic
  const sortedAppointments = useMemo(() => {
    if (!sortField) return appointments;

    const sorted = [...appointments].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case "date":
          comparison = a.date.getTime() - b.date.getTime();
          break;
        case "amount":
          comparison = (a.amount ?? 0) - (b.amount ?? 0);
          break;
        case "customer": {
          const customerA = customers.find((c) => c.id === a.customerId);
          const customerB = customers.find((c) => c.id === b.customerId);
          const nameA = a.customerName || customerA?.name || "";
          const nameB = b.customerName || customerB?.name || "";
          comparison = nameA.localeCompare(nameB);
          break;
        }
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [appointments, sortField, sortDirection, customers]);

  const totalPages = Math.ceil(sortedAppointments.length / ITEMS_PER_PAGE);

  const paginatedAppointments = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return sortedAppointments.slice(startIndex, endIndex);
  }, [sortedAppointments, currentPage]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    return sortDirection === "asc" ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4" />
    );
  };

  // Bulk selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(paginatedAppointments.map((apt) => apt.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkStatusUpdate = async (newStatus: AppointmentStatus) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    try {
      await Promise.all(ids.map((id) => onStatusUpdate(id, newStatus)));
      setSelectedIds(new Set());
      toast.success(
        `Updated ${ids.length} appointment(s) to ${newStatus.replace(/-/g, " ")}`
      );
    } catch (error) {
      // Error already handled in onStatusUpdate
    }
  };

  const isAllSelected =
    paginatedAppointments.length > 0 &&
    paginatedAppointments.every((apt) => selectedIds.has(apt.id));
  const isSomeSelected = selectedIds.size > 0 && !isAllSelected;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("ellipsis");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("ellipsis");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Reset to page 1 when appointments data changes (from filters or refresh)
  useEffect(() => {
    setCurrentPage(1);
  }, [appointments]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Appointments ({appointments.length})</CardTitle>
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {selectedIds.size} selected
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    Bulk Actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    onClick={() => handleBulkStatusUpdate("CONFIRMED")}
                  >
                    Mark as Confirmed
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleBulkStatusUpdate("COMPLETED")}
                  >
                    Mark as Completed
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleBulkStatusUpdate("CANCELLED")}
                  >
                    Mark as Cancelled
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleBulkStatusUpdate("PENDING")}
                  >
                    Mark as Confirmation Pending
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleBulkStatusUpdate("IN_PROGRESS")}
                  >
                    Mark as In Progress
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleBulkStatusUpdate("NOT_COMPLETED")}
                  >
                    Mark as Not Completed
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleBulkStatusUpdate("POSTPONED")}
                  >
                    Mark as Postponed
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedIds(new Set())}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto -mx-6 px-6">
          <Table className="min-w-[800px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={
                      isAllSelected
                        ? true
                        : isSomeSelected
                          ? "indeterminate"
                          : false
                    }
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead>Booking Ref</TableHead>
                <TableHead>
                  <button
                    onClick={() => handleSort("date")}
                    className="flex items-center hover:text-primary transition-colors"
                  >
                    Date
                    <SortIcon field="date" />
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    onClick={() => handleSort("customer")}
                    className="flex items-center hover:text-primary transition-colors"
                  >
                    Customer
                    <SortIcon field="customer" />
                  </button>
                </TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Staff</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">
                  <button
                    onClick={() => handleSort("amount")}
                    className="flex items-center justify-end ml-auto hover:text-primary transition-colors"
                  >
                    Amount
                    <SortIcon field="amount" />
                  </button>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {appointments.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="text-center text-muted-foreground"
                  >
                    No appointments found
                  </TableCell>
                </TableRow>
              ) : isLoading ? (
                [...Array(ITEMS_PER_PAGE)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={9}>
                      <Skeleton className="h-12 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                paginatedAppointments.map((appointment) => {
                  const customer = customers.find(
                    (c) => c.id === appointment.customerId
                  );
                  const appointmentVehicles = vehicles.filter((v) =>
                    appointment.vehicleIds?.includes(v.id)
                  );
                  const assignedStaff = appointment.staffId
                    ? staff.find((s) => s.id === appointment.staffId)
                    : null;
                  const isUpdating = updatingStatusId === appointment.id;
                  const isSubscriptionAppointment =
                    appointment.type === "USER_SUBSCRIPTION" ||
                    appointment.bookingType === "SUBSCRIPTION";

                  return (
                    <TableRow key={appointment.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(appointment.id)}
                          onCheckedChange={(checked) =>
                            handleSelectOne(appointment.id, checked as boolean)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm">
                          {appointment.bookingRef || "-"}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(appointment.date)}</TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span>
                            {appointment.customerName ||
                              customer?.name ||
                              "N/A"}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {customer?.phone || ""}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {appointmentVehicles.length > 0
                          ? appointmentVehicles
                              .map((v) => `${v.make} ${v.model}`)
                              .join(", ")
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {appointment.serviceName ||
                            (appointment.type || "").replace(/_/g, " ")}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        {assignedStaff ? (
                          <Badge variant="secondary" className="text-xs">
                            {assignedStaff.name}
                          </Badge>
                        ) : (
                          "Unassigned"
                        )}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(appointment.status)}
                      </TableCell>
                      <TableCell className="text-right">
                        {isSubscriptionAppointment ? (
                          <span className="text-muted-foreground">
                            Included in subscription
                          </span>
                        ) : appointment.amount !== null ? (
                          <div className="flex items-center justify-end gap-1">
                            {(appointment.platinumDiscount ?? 0) > 0 && (
                              <Crown className="h-3 w-3 text-amber-500" title="Platinum Member Discount Applied" />
                            )}
                            <span>
                              {formatCurrency(
                                (appointment.amount ?? 0) -
                                  (appointment.discount ?? 0) -
                                  (appointment.platinumDiscount ?? 0)
                              )}
                            </span>
                            {(appointment.platinumDiscount ?? 0) > 0 && (
                              <span className="text-xs text-muted-foreground line-through ml-1">
                                {formatCurrency(appointment.amount ?? 0)}
                              </span>
                            )}
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={isUpdating}
                              >
                                {isUpdating ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  "Change Status"
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {appointment.status !== "CANCELLED" && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    setStatusUpdateDialog({
                                      open: true,
                                      appointmentId: appointment.id,
                                      newStatus: "CANCELLED",
                                    })
                                  }
                                  className="text-red-600"
                                >
                                  Cancelled
                                </DropdownMenuItem>
                              )}
                              {appointment.status !== "COMPLETED" && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    setStatusUpdateDialog({
                                      open: true,
                                      appointmentId: appointment.id,
                                      newStatus: "COMPLETED",
                                    })
                                  }
                                >
                                  Completed
                                </DropdownMenuItem>
                              )}
                              {appointment.status !== "PENDING" && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    setStatusUpdateDialog({
                                      open: true,
                                      appointmentId: appointment.id,
                                      newStatus: "PENDING",
                                    })
                                  }
                                >
                                  Confirmation Pending
                                </DropdownMenuItem>
                              )}
                              {appointment.status !== "CONFIRMED" && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    setStatusUpdateDialog({
                                      open: true,
                                      appointmentId: appointment.id,
                                      newStatus: "CONFIRMED",
                                    })
                                  }
                                >
                                  Confirmed
                                </DropdownMenuItem>
                              )}
                              {appointment.status !== "IN_PROGRESS" && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    setStatusUpdateDialog({
                                      open: true,
                                      appointmentId: appointment.id,
                                      newStatus: "IN_PROGRESS",
                                    })
                                  }
                                >
                                  In Progress
                                </DropdownMenuItem>
                              )}
                              {appointment.status !== "NOT_COMPLETED" && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    setStatusUpdateDialog({
                                      open: true,
                                      appointmentId: appointment.id,
                                      newStatus: "NOT_COMPLETED",
                                    })
                                  }
                                  className="text-orange-600"
                                >
                                  Not Completed
                                </DropdownMenuItem>
                              )}
                              {appointment.status !== "POSTPONED" && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    setStatusUpdateDialog({
                                      open: true,
                                      appointmentId: appointment.id,
                                      newStatus: "POSTPONED",
                                    })
                                  }
                                  className="text-indigo-600"
                                >
                                  Postponed
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingAppointment(appointment)}
                            disabled={isUpdating}
                            title="Edit appointment"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onView(appointment)}
                            disabled={isUpdating}
                            title="View details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        {sortedAppointments.length > ITEMS_PER_PAGE && (
          <div className="mt-4">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage > 1) {
                        handlePageChange(currentPage - 1);
                      }
                    }}
                    className={
                      currentPage === 1
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
                {getPageNumbers().map((page, index) => {
                  if (page === "ellipsis") {
                    return (
                      <PaginationItem key={`ellipsis-${index}`}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    );
                  }
                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(page);
                        }}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage < totalPages) {
                        handlePageChange(currentPage + 1);
                      }
                    }}
                    className={
                      currentPage === totalPages
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </CardContent>

      {/* Status Update Confirmation Dialog */}
      <AlertDialog
        open={statusUpdateDialog.open}
        onOpenChange={(open) =>
          setStatusUpdateDialog({ ...statusUpdateDialog, open })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Status Update</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to change this appointment status to{" "}
              <strong>{statusUpdateDialog.newStatus}</strong>? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (statusUpdateDialog.newStatus) {
                  setUpdatingStatusId(statusUpdateDialog.appointmentId);
                  try {
                    await onStatusUpdate(
                      statusUpdateDialog.appointmentId,
                      statusUpdateDialog.newStatus
                    );
                    setStatusUpdateDialog({
                      open: false,
                      appointmentId: "",
                      newStatus: null,
                    });
                  } finally {
                    setUpdatingStatusId(null);
                  }
                }
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Dialog */}
      <AppointmentEditDialog
        open={!!editingAppointment}
        onOpenChange={(open) => !open && setEditingAppointment(null)}
        appointment={editingAppointment}
        customers={customers}
        vehicles={vehicles}
        staff={staff}
      />
    </Card>
  );
}
